import { Dispatch, SetStateAction, createContext, useEffect, useState } from 'react';
import i18n from 'src/translation/i18n';
import { IEmployeeWithDepartment, UserInfo } from 'src/types/master_data/employee.type';
import type { ResourceShort } from 'src/types/master_data/resource.type';
import { IRole } from 'src/types/master_data/role.type';
import type { IResourceFullAccessDetail } from 'src/types/master_data/userResource.type';

import { Language, Theme } from 'src/types/webConfig.type';

interface AppContextInterface {
  ticketSelected: string | undefined;
  setTicketSelected: Dispatch<SetStateAction<string | undefined>>;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  isTicketDetailCollapsed: boolean;
  setIsTicketDetailCollapsed: Dispatch<SetStateAction<boolean>>;
  isDetailSidebarCollapsed: boolean;
  setIsDetailSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  theme: Theme | undefined;
  setTheme: Dispatch<SetStateAction<Theme | undefined>>;
  language: Language | undefined;
  setLanguage: Dispatch<SetStateAction<Language | undefined>>;
  userInfo: UserInfo | null;
  setUserInfo: Dispatch<SetStateAction<UserInfo | null>>;
  userInfoQr: IEmployeeWithDepartment | undefined;
  setUserInfoQr: Dispatch<SetStateAction<IEmployeeWithDepartment | undefined>>;
  resources: IResourceFullAccessDetail[] | undefined;
  setResources: Dispatch<SetStateAction<IResourceFullAccessDetail[] | undefined>>;
  roles: IRole[] | undefined;
  setRoles: Dispatch<SetStateAction<IRole[] | undefined>>;
  qrCode: string | undefined;
  setQrCode: Dispatch<SetStateAction<string | undefined>>;
  isUserAdmin: boolean | undefined;
  setIsUserAdmin: Dispatch<SetStateAction<boolean | undefined>>;
  isSupportUser: boolean | undefined;
  setIsSupportUser: Dispatch<SetStateAction<boolean | undefined>>;
  selectedTicks: string[];
  setSelectedTicks: Dispatch<SetStateAction<string[]>>;
  resourcePermissionLoading: boolean | undefined;
  setResourcePermissionLoading: Dispatch<SetStateAction<boolean | undefined>>;
  rootResources: ResourceShort[];
  setRootResources: Dispatch<SetStateAction<ResourceShort[]>>;
  rootResource: ResourceShort | undefined;
  setRootResource: Dispatch<SetStateAction<ResourceShort | undefined>>;
  currentResource: ResourceShort | undefined;
  setCurrentResource: Dispatch<SetStateAction<ResourceShort | undefined>>;
  openModalSalary: boolean;
  setOpenModalSalary: Dispatch<SetStateAction<boolean>>;
  salaryTicketData: any;
  setSalaryTicketData: Dispatch<SetStateAction<any>>;
}

const userInfoJson = localStorage.getItem('userInfo');
const isAdminJson = localStorage.getItem('isAdmin');

const initialAppContext: AppContextInterface = {
  ticketSelected: undefined,
  setTicketSelected: () => null,
  isSidebarCollapsed: false,
  setIsSidebarCollapsed: () => null,
  isDetailSidebarCollapsed: false,
  setIsDetailSidebarCollapsed: () => null,
  isTicketDetailCollapsed: true,
  setIsTicketDetailCollapsed: () => null,
  theme: 'light',
  setTheme: () => null,
  language: 'vi',
  setLanguage: () => null,
  userInfo: userInfoJson ? JSON.parse(userInfoJson) : null,
  setUserInfo: () => null,
  userInfoQr: undefined,
  setUserInfoQr: () => null,
  resources: undefined,
  setResources: () => null,
  roles: [],
  setRoles: () => null,
  qrCode: undefined,
  setQrCode: () => null,
  isUserAdmin: isAdminJson ? JSON.parse(isAdminJson) : null,
  setIsUserAdmin: () => null,
  isSupportUser: false,
  setIsSupportUser: () => null,
  selectedTicks: [],
  setSelectedTicks: () => null,
  resourcePermissionLoading: undefined,
  setResourcePermissionLoading: () => null,
  rootResources: [],
  setRootResources: () => null,
  rootResource: undefined,
  setRootResource: () => null,
  currentResource: undefined,
  setCurrentResource: () => null,
  openModalSalary: false,
  setOpenModalSalary: () => null,
  salaryTicketData: undefined,
  setSalaryTicketData: () => null
};

export const AppContext = createContext<AppContextInterface>(initialAppContext);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [ticketSelected, setTicketSelected] = useState<string | undefined>(initialAppContext.ticketSelected);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(initialAppContext.isSidebarCollapsed);
  const [isTicketDetailCollapsed, setIsTicketDetailCollapsed] = useState(initialAppContext.isTicketDetailCollapsed);
  const [isDetailSidebarCollapsed, setIsDetailSidebarCollapsed] = useState(initialAppContext.isDetailSidebarCollapsed);
  const [theme, setTheme] = useState<Theme | undefined>(initialAppContext.theme);
  const [language, setLanguage] = useState<Language | undefined>(initialAppContext.language);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(initialAppContext.userInfo);
  const [resources, setResources] = useState<IResourceFullAccessDetail[] | undefined>(initialAppContext.resources);
  const [roles, setRoles] = useState<IRole[] | undefined>(initialAppContext.roles);
  const [isUserAdmin, setIsUserAdmin] = useState<boolean | undefined>(initialAppContext.isUserAdmin);
  const [isSupportUser, setIsSupportUser] = useState<boolean | undefined>(initialAppContext.isSupportUser);
  // Dùng cho chọn nhiều phiếu để xử lý nhanh
  const [selectedTicks, setSelectedTicks] = useState<string[]>(initialAppContext.selectedTicks);
  const [resourcePermissionLoading, setResourcePermissionLoading] = useState<boolean | undefined>(false);
  const [rootResources, setRootResources] = useState<ResourceShort[]>(initialAppContext.rootResources);
  const [rootResource, setRootResource] = useState<ResourceShort | undefined>(initialAppContext.rootResource);
  const [currentResource, setCurrentResource] = useState<ResourceShort | undefined>(initialAppContext.currentResource);

  // Sub page
  const [userInfoQr, setUserInfoQr] = useState<IEmployeeWithDepartment | undefined>(initialAppContext.userInfoQr);
  const [qrCode, setQrCode] = useState<string | undefined>(initialAppContext.qrCode);
  const [openModalSalary, setOpenModalSalary] = useState(false);
  const [salaryTicketData, setSalaryTicketData] = useState<any>(null);

  useEffect(() => {
    i18n.language === 'en' ? i18n.changeLanguage('vi') : i18n.changeLanguage('en');
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  return (
    <AppContext.Provider
      value={{
        ticketSelected,
        setTicketSelected,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        isDetailSidebarCollapsed,
        setIsDetailSidebarCollapsed,
        isTicketDetailCollapsed,
        setIsTicketDetailCollapsed,
        theme,
        setTheme,
        language,
        setLanguage,
        userInfo,
        setUserInfo,
        userInfoQr,
        setUserInfoQr,
        resources,
        setResources,
        roles,
        setRoles,
        qrCode,
        setQrCode,
        isUserAdmin,
        setIsUserAdmin,
        isSupportUser,
        setIsSupportUser,
        selectedTicks,
        setSelectedTicks,
        rootResources,
        setRootResources,
        rootResource,
        setRootResource,
        resourcePermissionLoading,
        setResourcePermissionLoading,
        currentResource,
        setCurrentResource,
        openModalSalary,
        setOpenModalSalary,
        salaryTicketData,
        setSalaryTicketData
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
